import { useQueries, type UseQueryResult } from '@tanstack/react-query';

import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	type LeadsTableRow,
	type LeadStageType,
	type PaginatedResponse,
} from '@ekhodealer/ekho-common';
import { toast } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { queries } from '@config/server/queryKeys';

import { useGetPipelines } from '../api/useGetPipelines';
import { searchLeads } from '../api/useSearchLeads';
import { useUpdateLeadStage } from '../api/useUpdateLeadStage';
import { leadsParamsAtom,leadTabFilterAtom } from '../atoms/leadsAtoms';
import { pipelineConfigFromLeadsTab } from '../utils/utils';

const ITEMS_PER_BATCH = 20;

/**
 * Column data structure for Kanban board
 * Represents a stage column with its leads
 */
export interface ColumnData {
	id: string; // Stage type (e.g., "NEW_LEAD")
	title: string; // Display name (e.g., "New Lead")
	dropMessage: string;
	items: LeadsTableRow[];
	hasMoreItems: boolean;
	nextCursor?: string;
	totalItemCount: number;
}

/**
 * Return type for useManageLeads hook
 */
export interface UseManageLeadsReturn {
	columnsData: ColumnData[];
	loadMore: (columnId: string) => Promise<void>;
	moveItem: (itemId: string, toStage: string) => Promise<void>;
}

/**
 * Orchestration hook for managing leads in Kanban board view
 * Uses useQueries with manual cursor-based pagination for dynamic stage management
 */
export const useManageLeads = (): UseManageLeadsReturn => {
	const servicesProviderId = useCurrentServicesProviderId();

	// Read from atoms
	const selectedTab = useAtomValue(leadTabFilterAtom);
	const leadsParams = useAtomValue(leadsParamsAtom);

	// Extract base filters (excluding stage since each column has its own stage)
	// This includes all applied filters: actionRequired, prequalified, leadType,
	// vehicle models, deal amount, dealer names, date ranges, etc.
	const baseFilters = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured to exclude stage from base filters
		const { stage, ...otherFilters } = leadsParams.filters || {};
		return otherFilters;
	}, [leadsParams.filters]);

	// Fetch pipelines configuration
	const { data: pipelines = {} } = useGetPipelines();

	// Derive selected pipeline configuration from tab
	const selectedPipelineConfig = useMemo(
		() => pipelineConfigFromLeadsTab(selectedTab, pipelines),
		[selectedTab, pipelines],
	);

	// Manual pagination state for cursor-based pagination
	const [stageCursors, setStageCursors] = useState<Record<string, string | undefined>>({});
	// Track accumulated data for each stage (all pages combined)
	const [stagePages, setStagePages] = useState<Record<string, LeadsTableRow[]>>({});

	// Fetch leads for all stages using useQueries with manual cursor management
	const stageQueries = useQueries({
		queries:
			selectedPipelineConfig?.stages.map((stage) => ({
				// Query key includes stage and cursor for cache separation
				queryKey: queries.leads.list(servicesProviderId, {
					filters: {
						...baseFilters,
						stage: stage.stage,
					},
					limit: ITEMS_PER_BATCH,
					cursor: stageCursors[stage.stage],
					query: leadsParams.query,
				}).queryKey,

				// Query function fetches data for current cursor
				queryFn: () =>
					searchLeads(servicesProviderId, {
						filters: {
							...baseFilters,
							stage: stage.stage,
						},
						limit: ITEMS_PER_BATCH,
						cursor: stageCursors[stage.stage],
						query: leadsParams.query,
					}),

				staleTime: Infinity,
			})) || [],
	}) as UseQueryResult<PaginatedResponse<LeadsTableRow>, Error>[];

	// Accumulate data from queries into stagePages state
	// This handles first page (replace) vs subsequent pages (append)
	useEffect(() => {
		if (!selectedPipelineConfig) return;

		// Build new state only if there are actual changes
		setStagePages((prev) => {
			let hasChanges = false;
			const newState = { ...prev };

			stageQueries.forEach((query, index) => {
				const stage = selectedPipelineConfig.stages[index];
				if (!query.data || !stage) return;

				const stageId = stage.stage;
				const cursor = stageCursors[stageId];
				const existingData = prev[stageId] || [];
				const newData = query.data.data;

				if (cursor === undefined) {
					// First page - check if data actually changed
					const dataChanged =
						existingData.length !== newData.length ||
						!existingData.every((item, i) => item.id === newData[i]?.id);

					if (dataChanged) {
						newState[stageId] = newData;
						hasChanges = true;
					}
				} else {
					// Subsequent page - append only genuinely new items
					const existingIds = new Set(existingData.map((item) => item.id));
					const newItems = newData.filter((item) => !existingIds.has(item.id));

					if (newItems.length > 0) {
						newState[stageId] = [...existingData, ...newItems];
						hasChanges = true;
					}
				}
			});

			// Only return new state if there were actual changes
			// This prevents unnecessary re-renders
			if (!hasChanges) {
				return prev;
			}

			return newState;
		});
	}, [selectedPipelineConfig, stageCursors, stageQueries]);

	// Reset pagination state when any filter changes
	useEffect(() => {
		setStageCursors({});
		setStagePages({});
	}, [leadsParams]);

	// Derive columnsData from accumulated stagePages state
	const columnsData = useMemo(() => {
		if (!selectedPipelineConfig) {
			return [];
		}

		return selectedPipelineConfig.stages.map((stageConfig, index) => {
			const query = stageQueries[index];
			const items = stagePages[stageConfig.stage] || [];
			const latestResponse = query?.data;

			return {
				id: stageConfig.stage,
				title: stageConfig.name,
				dropMessage: `Move to ${stageConfig.name}`,
				items,
				hasMoreItems: latestResponse?.hasMore ?? false,
				nextCursor: latestResponse?.nextCursor ?? undefined,
				totalItemCount: latestResponse?.count ?? items.length,
			};
		});
	}, [selectedPipelineConfig, stagePages, stageQueries]);

	/**
	 * Load more items for a specific column by updating the cursor state
	 * This triggers a new query fetch with the next cursor
	 *
	 * @param columnId - Stage ID to load more leads for
	 */
	const loadMore = useCallback(
		async (columnId: string) => {
			if (!selectedPipelineConfig) {
				return;
			}

			// Find the index of the stage to load more for
			const stageIndex = selectedPipelineConfig.stages.findIndex(
				(stage) => stage.stage === columnId,
			);
			if (stageIndex === -1) {
				return;
			}

			const query = stageQueries[stageIndex];
			const nextCursor = query?.data?.nextCursor;
			const hasMore = query?.data?.hasMore;

			// Only load more if there's more data available
			if (!nextCursor || !hasMore) {
				return;
			}

			// Skip if we already requested this cursor (prevents duplicate requests)
			const currentCursor = stageCursors[columnId];
			if (currentCursor === nextCursor) {
				return;
			}

			// Update cursor state to trigger refetch with new cursor
			setStageCursors((prev) => ({ ...prev, [columnId]: nextCursor }));
		},
		[selectedPipelineConfig, stageQueries, stageCursors],
	);

	// Delegate mutation to specialized hook
	const { mutateAsync: updateLeadStageMutation } = useUpdateLeadStage();

	/**
	 * Move a lead to a different stage with optimistic updates
	 * Delegates to useUpdateLeadStage hook for optimistic update logic
	 *
	 * @param itemId - Lead ID to move
	 * @param toStageId - Target stage enum ID (e.g., "CONTACTED")
	 */
	const moveItem = useCallback(
		async (itemId: string, toStageId: string) => {
			if (!servicesProviderId || !selectedPipelineConfig) {
				toast({
					message: 'Unable to move lead. Please refresh and try again.',
					variant: 'error',
				});
				throw new Error('Services provider ID and pipeline are required');
			}

			// Find the item and its source column
			const sourceColumn = columnsData.find((col) =>
				col.items.some((item: LeadsTableRow) => item.id === itemId),
			);
			const itemToMove = sourceColumn?.items.find(
				(item: LeadsTableRow) => item.id === itemId,
			);

			if (!itemToMove || !sourceColumn) {
				toast({
					message: 'Lead not found. Please refresh and try again.',
					variant: 'error',
				});
				throw new Error('Item not found');
			}

			// Find target stage config to get formatted name
			const targetStageConfig = selectedPipelineConfig.stages.find(
				(s) => s.stage === toStageId,
			);
			if (!targetStageConfig) {
				toast({
					message: 'Invalid target stage. Please try again.',
					variant: 'error',
				});
				throw new Error('Target stage not found in pipeline configuration');
			}

			try {
				// Delegate to mutation hook with optimistic update context
				await updateLeadStageMutation({
					leadId: itemId,
					servicesProviderId,
					stage: toStageId as LeadStageType,
					stageReason: 'Updated by user',
					optimisticUpdateContext: {
						sourceStage: sourceColumn.id,
						targetStageName: targetStageConfig.name,
						isKanbanView: true,
						currentQueryParams: {
							filters: baseFilters,
							limit: ITEMS_PER_BATCH,
							query: leadsParams.query,
						},
					},
				});

				toast({
					message: `Lead moved to ${targetStageConfig.name}`,
					variant: 'success',
				});
			} catch (error) {
				toast({
					message: 'Failed to move lead. Changes have been reverted.',
					variant: 'error',
				});

				throw error;
			}
		},
		[
			columnsData,
			selectedPipelineConfig,
			servicesProviderId,
			leadsParams,
			baseFilters,
			updateLeadStageMutation,
		],
	);

	return {
		columnsData,
		loadMore,
		moveItem,
	};
};
